<template>
  <SiteHeader :tickets="false">
    <!-- Opens the preview first; on the reservation page itself it is just where you are -->
    <Button
      variant="outline"
      :to="onReservation ? { name: 'kleding-reserveren' } : undefined"
      :type="onReservation ? undefined : 'button'"
      :aria-haspopup="onReservation ? undefined : 'dialog'"
      :aria-current="onReservation ? 'page' : undefined"
      :class="$style['layout__bag']"
      @click="!onReservation && show()">
      <Icon name="ShoppingBag" :class="$style['layout__bag-icon']" />
      <span :class="$style['layout__bag-label']">Mijn reservering</span>
      <span v-if="itemCount" :key="bump" :class="[$style['layout__bag-count'], bump && $style['layout__bag-count--bump']]">
        <span :class="$style['layout__sr-only']">, </span>{{ itemCount }}<span :class="$style['layout__sr-only']">{{ itemCount === 1 ? ' item' : ' items' }}</span>
      </span>
    </Button>
  </SiteHeader>
  <KeepAlive>
    <main :class="$style['layout__main']">
      <slot />
    </main>
  </KeepAlive>
  <SiteFooter />
  <ShoppingBagPreview />
</template>
<script setup>
const { load, navigation } = useNavigation()
const { formatDefault, formatBreadcrumbs } = useHrMeta()
const route = useRoute()
const { itemCount, loaded } = useShoppingBag()
const { show } = useBagPreview()
const onReservation = computed(() => route.name === 'kleding-reserveren')
useStageTheme()

// The count bumps when something is added, not when the saved reservation comes back on load
const bump = ref(0)
let counting = false
watch(loaded, isLoaded => { if (isLoaded) { nextTick(() => { counting = true }) } }, { immediate: true })
watch(itemCount, (now, before) => { if (counting && now > before) { bump.value++ } })
formatDefault()

watch(() => route.path, () => {
  formatBreadcrumbs(navigation)
})
await load()
</script>
<style module>
.layout__main {
  /* Short pages may end sooner instead of leaving a black void above the footer */
  min-height: 60svh;
}

.layout__bag {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.layout__bag-icon {
  flex: none;
  width: 1.25rem;
  height: 1.25rem;
}

.layout__bag-label {
  margin-left: var(--spacing);
}

.layout__bag-count {
  display: inline-grid;
  place-items: center;
  min-width: 1.5rem;
  height: 1.5rem;
  margin-left: var(--spacing);
  padding-inline: var(--spacing-s);
  border-radius: 999rem;
  background: var(--primary-color);
  color: var(--stage-black);
  font-size: var(--font-size-small);
  letter-spacing: 0;
}

.layout__bag-count--bump {
  animation: bag-bump 600ms var(--stage-ease);
}

@keyframes bag-bump {
  40% {
    transform: scale(1.45);
  }
}

.layout__sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (width <= 48rem) {
  .layout__bag-label {
    position: absolute;
    width: var(--stage-hairline);
    height: var(--stage-hairline);
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* Only the icon, with the count as a badge on the edge of the circle, like on an app icon.
     The button clips what sticks out, so here it may show. */
  header .layout__bag {
    --padding-x: var(--spacing-l);

    overflow: visible;
  }

  /* A black ring keeps it apart from the outline of the button */
  .layout__bag-count {
    position: absolute;
    top: calc(-1 * var(--spacing-xs));
    right: calc(-1 * var(--spacing-xs));
    min-width: 1.25rem;
    height: 1.25rem;
    margin: 0;
    padding-inline: var(--spacing-xs);
    box-shadow: 0 0 0 0.1875rem var(--stage-black);
    font-size: 0.75rem;
    line-height: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .layout__bag-count--bump {
    animation: none;
  }
}
</style>
